import { Stack, useTheme } from '@ltvco/refresh-lib/theme';
import { isWithinLast12Months } from '@ltvco/refresh-lib/utils';
import {
  type NavLinkData,
  type serializedTypes,
  type SerializedPropertyReport,
  type propertiesTypes,
} from '@ltvco/refresh-lib/v1';
import {
  Home,
  LocationOn,
  Gavel,
  TaskAlt,
  Group,
  Circle,
  MonetizationOn,
} from '@mui/icons-material';

import { getPropertyDetailsCount } from 'utils/propertyReportUtils';

interface PropertyNavLinkData extends NavLinkData {
  visible: boolean;
}

export const getPropertyNavLinkData = (
  potentialOwnersLength: number,
  possibleResidentsLength: number,
  buildingPermitsLength: number,
  deedsLength: number,
  liensLength: number,
  propertyDetailsLength: number,
  propertyValuesLength: number,
  equitiesCount: number,
  schoolsCount: number
) => {
  return [
    {
      href: '#overview',
      sectionId: '#overview',
      text: 'Overview',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'identity',
      name: 'overview',
    },
    {
      href: '#possible-owners-section',
      sectionId: '#possible-owners-section',
      text: 'Possible Owners',
      reportItemsCount: potentialOwnersLength,
      notificationSection: 'owners',
      name: 'possible-owners',
    },
    {
      href: '#possible-residents-section',
      sectionId: '#possible-residents-section',
      text: 'Possible Residents',
      reportItemsCount: possibleResidentsLength,
      notificationSection: 'residents',
      name: 'possible-residents',
    },
    {
      href: '#lot-and-building-details-section',
      sectionId: '#lot-and-building-details-section',
      text: 'Property Details',
      reportItemsCount: propertyDetailsLength,
      notificationSection: 'lot_details',
      notificationSectionMapping: [
        { text: 'lot_details', href: '#lot-and-building-details-section' },
        { text: 'values', href: '#assessed-value-and-taxes-section' },
      ],
      name: 'property-details',
    },
    {
      href: '#building-permits-section',
      sectionId: '#building-permits-section',
      text: 'Building Permits',
      reportItemsCount: buildingPermitsLength,
      notificationSection: 'permits',
      name: 'building-permits',
    },
    {
      href: '#deeds-section',
      sectionId: '#deeds-section',
      text: 'Deeds',
      reportItemsCount: deedsLength,
      notificationSection: 'deeds',
      name: 'deeds',
    },
    {
      href: '#liens-section',
      sectionId: '#liens-section',
      text: 'Liens',
      reportItemsCount: liensLength,
      notificationSection: 'liens',
      name: 'liens',
    },
    {
      href: '#estimated-value-section',
      sectionId: '#estimated-value-section',
      text: 'Property Valuation',
      reportItemsCount: propertyValuesLength,
      notificationSection: 'valuation',
      name: 'property-valuation',
    },
    {
      href: '#equity-and-loans-section',
      sectionId: '#equity-and-loans-section',
      text: 'Equity & Loans',
      reportItemsCount: equitiesCount,
      notificationSection: 'equity_loans',
      name: 'equity-&-loans',
    },
    {
      href: '#schools-nearby-section',
      sectionId: '#schools-nearby-section',
      text: 'Schools & Area',
      reportItemsCount: schoolsCount,
      notificationSection: 'schools',
      notificationSectionMapping: [
        { text: 'schools', href: '#schools-nearby-section' },
        { text: 'parcel_address', href: '#demographics-section' },
      ],
      name: 'schools-&-area',
    },
  ].filter(Boolean) as PropertyNavLinkData[];
};

// V2 Property Report Navigation Menu
export const getPropertyNavLinkDataV2 = (
  reportData: SerializedPropertyReport,
  marketSortedBySource: propertiesTypes.MarketValues[]
) => {
  const property: serializedTypes.SerializedProperty = reportData.properties[0];
  const theme = useTheme();

  // People
  const owners = property.owners.length ?? 0;
  const residents = property.residents.length ?? 0;

  // Property
  const propertyDetailsCount = getPropertyDetailsCount(property);
  const building_permits = property.building_permits.length ?? 0;
  const hoas = property.hoas.length ?? 0;
  const taxes =
    property.taxes?.bills?.filter(
      (bill) => Boolean(bill.amount) && Boolean(bill.year)
    ).length ?? 0;
  const equitiesCount = property.equities.length ?? 0;
  const marketValuesCount = marketSortedBySource.reduce((count, value) => {
    const hasValidItem = value.some((val) =>
      isWithinLast12Months(new Date(val.date?.full ?? ''))
    );
    return count + (hasValidItem ? 1 : 0);
  }, 0);
  const propertyValuesCount = equitiesCount + marketValuesCount;

  // Legal & Recorded Docs
  const deeds = property.deeds.length ?? 0;
  const liens = property.courts.civils.length ?? 0;

  // Market & Area Insights
  const demographicsCount = 1;
  const schools = reportData.schools.length ?? 0;

  return [
    {
      href: '#property-overview',
      labelText: 'Overview',
      name: 'overview',
      icons: [
        <Stack position="relative" alignItems="center" justifyContent="center">
          <Circle
            sx={{ color: theme.palette.primary.main, fontSize: '2.5rem' }}
          />
          <Home
            sx={{
              color: theme.palette.primary.contrastText,
              fontSize: '1.5rem',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
            }}
          />
        </Stack>,
      ],
      notificationSection: 'identity',
    },
    {
      labelText: 'Property',
      name: 'property',
      icons: [
        <Home
          key="property-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Property Details',
          name: 'property-details',
          href: '#lot-and-building-details-section',
          reportItemsCount: propertyDetailsCount,
          notificationSection: 'lot_details',
        },
        {
          labelText: 'Taxes',
          name: 'taxes',
          href: '#assessed-value-and-taxes-section',
          reportItemsCount: taxes,
          notificationSection: 'taxes',
        },
        {
          labelText: 'Building Permits',
          name: 'building-permits',
          href: '#building-permits-section',
          reportItemsCount: building_permits,
          notificationSection: 'permits',
        },
        {
          labelText: 'HOA',
          name: 'hoa',
          href: '#homeowner-association-section',
          reportItemsCount: hoas,
          notificationSection: 'hoa',
        },
      ],
    },
    {
      labelText: 'Valuation & Loans',
      name: 'valuation-&-loans',
      icons: [
        <MonetizationOn
          key="valuation-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Valuation History',
          name: 'valuation-history',
          href: '#property-valuation-section',
          reportItemsCount: propertyValuesCount,
          notificationSection: 'valuation',
        },
        {
          labelText: 'Equity & Loans',
          name: 'equity-&-loans',
          href: '#equity-and-loans-section',
          reportItemsCount: equitiesCount,
          notificationSection: 'equity_loans',
        },
      ],
    },
    {
      labelText: 'People',
      name: 'people',
      icons: [
        <Group
          key="people-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Possible Owners',
          name: 'possible-owners',
          href: '#possible-owners-section',
          reportItemsCount: owners,
          notificationSection: 'owners',
        },
        {
          labelText: 'Possible Residents',
          name: 'possible-residents',
          href: '#possible-residents-section',
          reportItemsCount: residents,
          notificationSection: 'residents',
        },
      ],
    },
    {
      labelText: 'Legal & Deeds',
      name: 'legal-&-deeds',
      icons: [
        <Gavel
          key="legal-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Deeds',
          name: 'deeds',
          href: '#deeds-section',
          reportItemsCount: deeds,
          notificationSection: 'deeds',
        },
        {
          labelText: 'Liens',
          name: 'liens',
          href: '#liens-section',
          reportItemsCount: liens,
          notificationSection: 'liens',
        },
      ],
    },
    {
      labelText: 'Local Market Insights',
      name: 'local-market-insights',
      icons: [
        <LocationOn
          key="market-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Market Insights',
          name: 'market-insights',
          href: '#market-insights',
          hideReportItemsCount: true,
          notificationSection: 'market_insights',
        },
        {
          labelText: 'Demographics',
          name: 'demographics',
          href: '#demographics-section',
          reportItemsCount: demographicsCount,
          notificationSection: 'demographics',
        },
        {
          labelText: 'Schools',
          name: 'schools',
          href: '#schools-nearby-section',
          reportItemsCount: schools,
          notificationSection: 'schools',
        },
        {
          labelText: 'Natural Hazards',
          name: 'natural-hazards',
          href: '#natural-hazards-section',
          hideReportItemsCount: true,
          notificationSection: 'natural_hazards',
        },
      ],
    },
    {
      labelText: 'Next Steps',
      name: 'next-steps',
      icons: [
        <TaskAlt
          key="next-steps-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
        />,
      ],
      childItems: [
        {
          labelText: 'Notes',
          name: 'notes',
          href: '#Notes-section',
          hideReportItemsCount: true,
          notificationSection: 'notes',
        },
        {
          href: '#related-reports-section',
          labelText: 'Related Reports',
          hideReportItemsCount: true,
          name: 'related-reports',
        },
        {
          labelText: 'Report Rating',
          name: 'report-rating',
          href: '#report-rating',
          hideReportItemsCount: true,
          notificationSection: 'report_rating',
        },
      ],
    },
  ];
};
